import React, { useMemo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuItemWithNestedMenu } from 'ui-components/components/dropdown-menu-item-with-nested-menu';

import { useInventoryManagementBasicFeatureGate } from '../../../../../feature-gates/use-inventory-management-basic-feature-gate';
import { useFulfillmentPlanIdContext } from '../../../../../fulfillment-plans/context/fulfillment-plan-id-context-provider';
import { useEntitiesFulfillmentPlan } from '../../../../../hooks/entities/selectors/fulfillment-plans';
import { useInventoryAllocation } from '../../../../../hooks/inventory/use-inventory-allocation';
import { deallocateFulfillmentPlans } from '../../../../../store/inventory/inventory.actions';
import messages from '../messages';

const ALLOCATED = 'Allocated';
const UNALLOCATED = 'Unallocated';
const UNKNOWN = 'Unknown';

const AllocateShipmentMenuItem = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const fulfillmentPlanId = useFulfillmentPlanIdContext();
  const fulfillmentPlan = useEntitiesFulfillmentPlan(fulfillmentPlanId);
  const { allocateInventory, isAllocating } = useInventoryAllocation();

  const { isAllocated, isDeallocated } = useMemo(() => {
    const { allocationStatus } = fulfillmentPlan;
    return {
      isAllocated: allocationStatus === ALLOCATED,
      isDeallocated: allocationStatus === UNALLOCATED || allocationStatus === UNKNOWN,
    };
  }, [fulfillmentPlan]);

  const handleAllocation = useCallback(() => {
    allocateInventory([fulfillmentPlanId]);
  }, [fulfillmentPlanId, allocateInventory]);

  const handleDeallocation = useCallback(() => {
    dispatch(deallocateFulfillmentPlans({ fulfillmentPlanIds: [fulfillmentPlanId] }));
  }, [dispatch, fulfillmentPlanId]);

  const allocationDisabled = isAllocating || isAllocated;
  const deallocationDisabled = isAllocating || isDeallocated;

  const { isFeatureEnabled } = useInventoryManagementBasicFeatureGate();

  return (
    <DropdownMenuItemWithNestedMenu isLocked={!isFeatureEnabled} title={intl.formatMessage(messages.allocateShipment)}>
      <DropdownMenuItem disabled={allocationDisabled} onClick={handleAllocation} title={intl.formatMessage(messages.allocate)}>
        <FormattedMessage {...messages.allocate} />
      </DropdownMenuItem>
      <DropdownMenuItem disabled={deallocationDisabled} onClick={handleDeallocation} title={intl.formatMessage(messages.deallocate)}>
        <FormattedMessage {...messages.deallocate} />
      </DropdownMenuItem>
    </DropdownMenuItemWithNestedMenu>
  );
};

export default AllocateShipmentMenuItem;
